import { Button, Checkbox, Popconfirm, Popover, Select, Slider, Spin, Tabs, Tooltip, message } from 'antd';
import { MRT_ColumnDef, MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import React, { useEffect, useMemo, useState } from 'react';
import { DataIndexItem } from '../../../types/interfaces';
import { ReactComponent as DeleteIcon } from "../../../assets/svgs/icons/delete-icon.svg";
import { ReactComponent as EditIcon } from "../../../assets/svgs/icons/edit-icon.svg";
import { ReactComponent as ReloadIcon } from "../../../assets/svgs/icons/reload-icon.svg";
import { ReactComponent as SettingIcon } from "../../../assets/svgs/icons/setting-icon.svg";
import TranslationPanel from '../../tables/dataIndexTable/TranslationPanel';
import { EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons';
import { deleteDataItem, loadDataItem, removeBatch, removeItemsFromBatch } from '../../tables/dataIndexTable/DataIndexLambdaUtils';
import { DataIndexMenuTableContainer } from '../DataManager.style';
import { changeDataItem, convertStatus, failedStatus, getDataItemWithDataId, removeDataItem, resetOriginDataList } from '../../tables/dataIndexTable/DataIndexUtils';
import { changeIntensityScale, changeOpacity, existingPointCloudInViewer, loadPointCloud, removePointCloud, setMaterialAttribute, setPointSourcIdInPointCloud, translationPointCloud, updateActiveProjection, zoomTo } from '../../../common/potreeUtils';
import { DataIndexActionType } from '../../../common/constant';
import DataIndexMenuEditModal from '../../conversionMenu/dataIndexEditModal/DataIndexMenuEditModal';
import { changeDataItemStore, DataStore, getDataItemFromDataId } from '../../../store/DataStore';
const DefaultValue = 0;

interface BatchDetailsProps {
    selectedId: (string);
    expand: boolean
}

const BatchDetailsTable = ({ selectedId, expand }: BatchDetailsProps) => {
    const [dataLoading, setDataLoading] = useState<boolean>(false);
    const [removerLoader, setRemoveLoader] = useState<boolean>(false);
    const [batchInfo, setBatchInfo] = useState<DataIndexItem | undefined>(undefined);
    const [tableData, setTableData] = useState<DataIndexItem[]>([]);
    const [selectedDataItems, setSelectedDataItems] = useState<DataIndexItem[]>([]);
    const [selectedDataIndexRow, setSelectedDataIndexRow] = useState<DataIndexItem | undefined>(undefined);
    const [showDataIndexEditModal, setShowDataIndexEditModal] = useState(false);

    const columnsData = [
        { title: "", key: "show", size: 35 },
        { title: "Name", key: "name", size: 250, },
        { title: "EPSG", key: "raw_epsg", size: 100 },
        { title: "Epoch", key: "raw_epoch", size: 90 },
        { title: "Geoid", key: "raw_geoid", size: 90 },
        { title: "Acq", key: "acquisition_date", size: 150 },
        { title: "Type", key: "acquisition_type", size: 170 },
        { title: "Process Status", key: "conversion_status", size: 110 },
        { title: "Size (GB)", key: "raw_size", size: 80 },
        { title: "Action", key: "operation", size: 180, }
    ];


    const columns = useMemo<MRT_ColumnDef<any>[]>(() => {
        if (tableData.length === 0) {
            return [];
        }
        return columnsData.map(({ title, key, size }) => {
            if (key === "name") {
                return {
                    accessorKey: key,
                    enableSorting: false,
                    header: title || "",
                    size: size,
                    Cell: ({ row }) => {
                        const record: DataIndexItem = row.original;
                        let disabled = record.entwine_build_path === "";
                        if (!disabled) {
                            let isConversion = convertStatus.includes(record.conversion_status);
                            let isFailed = failedStatus.includes(record.conversion_status);
                            disabled = isConversion || isFailed;
                        }
                        return (
                            <div className='name_btn'>
                                <button
                                    className={`button-name${disabled ? "-disabled" : ""}`}
                                    type="button"
                                    disabled={disabled}
                                    onClick={() => onActionHandler(DataIndexActionType.ZoomTo, record.data_id, DefaultValue)}
                                >
                                    {record.name}
                                </button>
                            </div>
                        );
                    },
                };
            } else if (key === "show") {
                return {
                    accessorKey: key,
                    enableSorting: false,
                    header: title || "",
                    Header: ({ column }) => (
                        <>
                            <Checkbox
                                onChange={(e) => {
                                    const isChecked = e.target.checked;
                                    setSelectedDataItems(isChecked ? tableData : []);
                                }}
                            />
                        </>
                    ),
                    size: size,
                    Cell: ({ row }) => {
                        const record: DataIndexItem = row.original;
                        const selectBatch = (e: any) => {
                            setSelectedDataItems((prev) => {
                                const isChecked = e.target.checked;
                                if (!isChecked) {
                                    return prev.filter((item) => item.data_id !== record.data_id);
                                } else {
                                    if (prev.find((item) => item.data_id === record.data_id)) {
                                        return prev;
                                    } else {
                                        return [...prev, record];
                                    }
                                }
                            });
                        };
                        return (
                            <div style={{ width: size }} className='table_checkbox_section'>
                                <Checkbox onChange={selectBatch} checked={selectedDataItems.find((item) => item.data_id === record.data_id) ? true : false} />
                            </div>
                        );
                    }
                }
            } else if (key === "operation") {
                return {
                    accessorKey: key,
                    enableSorting: false,
                    header: title || "",
                    size: size,
                    Cell: ({ row }) => {
                        const record: DataIndexItem = row.original;
                        const showConfirm = () => {
                            const annotations = window.potreeViewer.scene.annotations.flatten();
                            const annotation = annotations.find((at: any) => at.title === record.name);
                            if (annotation) {
                                annotation.visible = !record.show;
                            }
                            onActionHandler(
                                record.show ? DataIndexActionType.Hide : DataIndexActionType.Show,
                                record.data_id,
                                DefaultValue
                            );
                        };
                        const editConfirm = () => onActionHandler(DataIndexActionType.Edit, record.data_id, DefaultValue);
                        const reloadConfirm = () => onActionHandler(DataIndexActionType.Reload, record.data_id, DefaultValue);
                        const deleteConfirm = () => onActionHandler(DataIndexActionType.Delete, record.data_id, DefaultValue);
                        const attributeChanged = (value: string) =>
                            onActionHandler(DataIndexActionType.Attribute, record.data_id, value);
                        const intensityChanged = (value: number) =>
                            onActionHandler(DataIndexActionType.Intensity, record.data_id, value);

                        return (
                            <div className="actionIconBox">
                                {record.entwine_build_path !== "" && (
                                    <>
                                        {record.show ? (
                                            <Tooltip title="Hide">
                                                <EyeInvisibleOutlined onClick={showConfirm} />
                                            </Tooltip>
                                        ) : (
                                            <Tooltip title="Show">
                                                <EyeOutlined onClick={showConfirm} />
                                            </Tooltip>
                                        )}
                                    </>
                                )}
                                <Tooltip title="Edit">
                                    <EditIcon key="EditIcon" title="Edit" color='#ffffff' onClick={editConfirm} />
                                </Tooltip>
                                <Tooltip title="Delete" placement="right">
                                    <Popconfirm
                                        placement="topRight"
                                        title="Are you sure to delete this item?"
                                        onConfirm={() => deleteConfirm()}
                                        okText="Yes"
                                        cancelText="No"
                                    >
                                        <DeleteIcon key="DeleteIcon" title="Delete" color='#ffffff' />
                                    </Popconfirm>
                                </Tooltip>

                                {record.entwine_build_path !== "" && (
                                    <>
                                        <TranslationPanel record={record} actionHandler={onActionHandler} />

                                        <Popover
                                            placement="bottomRight"
                                            content={
                                                <div style={{ width: 200 }}>
                                                    <h4>Attribute</h4>
                                                    <Select defaultValue={record.default_material} onChange={attributeChanged}>
                                                        <option value="rgba">RGB</option>
                                                        <option value="intensity">Intensity</option>
                                                        <option value="classification">Classification</option>
                                                    </Select>
                                                    <h4>Intensity Scale</h4>
                                                    <Slider
                                                        min={record.intensity_min}
                                                        max={record.intensity_max}
                                                        step={1}
                                                        marks={{
                                                            0: record.intensity_min,
                                                            1: record.intensity_max
                                                        }}
                                                        defaultValue={record.intensity_max}
                                                        onChange={intensityChanged}
                                                    />
                                                </div>
                                            }
                                            trigger="click"
                                        >
                                            <Tooltip title="Edit">
                                                <SettingIcon key="SettingIcon" title="Setting" color='#ffffff' style={{ pointerEvents: !record.loaded ? "none" : "auto" }} />
                                            </Tooltip>
                                        </Popover>
                                    </>
                                )}

                                {record.entwine_build_path !== "" && (
                                    <Tooltip title="Reload">
                                        <ReloadIcon key="ReloadIcon" title="Reload" color='#ffffff' onClick={reloadConfirm} />
                                    </Tooltip>
                                )}


                            </div>
                        );
                    }
                }
            } else {
                let data = {
                    accessorKey: key,
                    header: title || "",
                    size: size,
                }
                return data
            };

        })
    }, [tableData, selectedDataItems]);

    const table = useMaterialReactTable({
        columns,
        data: tableData,
        enableSorting: false,
        positionToolbarAlertBanner: 'none',
        rowNumberDisplayMode: "static",
        initialState: {
            showGlobalFilter: true,
            pagination: { pageSize: 10, pageIndex: 0 } // Set default rows per page to 5
        },
    });

    if (!table) {
        return null;
    }

    useEffect(() => {
        let batchData = getDataItemFromDataId(selectedId)
        setBatchInfo(batchData)
    }, []);

    useEffect(() => {

        if (!batchInfo) return

        let dataIds = batchInfo.source_ids || [];

        let dataArr: DataIndexItem[] = []
        for (let i = 0; i < dataIds.length; i++) {
            let data = getDataItemFromDataId(dataIds[i])
            if (data === undefined) continue;
            dataArr.push(data)
        }
        setTableData(dataArr)
    }, [batchInfo])

    const removeItems = (e: any) => {
        setRemoveLoader(true)
        setDataLoading(true);
        if (batchInfo?.owner_id === undefined) return;
        removeItemsFromBatch(selectedId, selectedDataItems.map(item => item.data_id), batchInfo?.owner_id).then((resp: any) => {
            // const data = JSON.parse(resp);
            let removedIds = resp.removed_items
            if (removedIds?.length) {
                setTableData(
                    tableData.filter(item => !removedIds.includes(item.data_id))
                )
            }
            message.success("Item removed successfully from batch.");
        }).catch((err) => {
            message.error(err);
        }).finally(() => {
            setRemoveLoader(false)
            setDataLoading(false);
        });
    }

    const onActionHandler = async (actionType: DataIndexActionType, dataId: string, value: any) => {
        const dataItem = getDataItemWithDataId(tableData, dataId);
        if (!dataItem) return;

        const pointCloud = existingPointCloudInViewer(dataItem);

        if (actionType === DataIndexActionType.Opacity && typeof value === "number") {
            changeOpacity(dataItem, value);
        } else if (actionType === DataIndexActionType.Attribute && typeof value === "string") {
            setMaterialAttribute(dataItem, value);
        } else if (actionType === DataIndexActionType.Intensity && typeof value === "number") {
            changeIntensityScale(dataItem, value);
        } else if (actionType === DataIndexActionType.Delete) {
            setDataLoading(true);
            if (dataItem.batch) {
                removeBatch(dataItem.data_id, dataItem.owner_id).then(() => {
                    message.success('Batch deleted successfully');
                    const temp = removeDataItem(tableData, dataItem.data_id);
                    setTableData(temp);
                    setDataLoading(false);
                }).catch((error) => {
                    setDataLoading(false);
                    message.error('Failed to delete batch');
                })
            } else {
                deleteDataItem(dataItem.data_id, dataItem.owner_id).then(() => {
                    message.success('Data deleted successfully');
                    const temp = removeDataItem(tableData, dataItem.data_id);
                    setTableData(temp);
                }).catch((error) => {
                    console.log(error);
                    message.error('Failed to delete data');
                }).finally(() => {
                    setDataLoading(false);
                })
            }

        } else if (actionType === DataIndexActionType.Reload) {
            setDataLoading(true);
            loadDataItem(dataItem.data_id, dataItem.owner_id).then(async (res) => {
                removePointCloud(pointCloud, dataItem);
                const uuid = await loadPointCloud(dataItem, false);
                const tempTableData = changeDataItem(tableData, dataId, {
                    ...res,
                    uuid: uuid,
                });
                // const tempTableData = changeDataItem(tableData, dataId, { uuid: uuid, show: true, loaded: true });
                setTableData(tempTableData);
                message.success('Data reloaded successfully');
            }).catch((error) => {
                console.log(error);
                message.error('Failed to reload data');
            }).finally(() => {
                setDataLoading(false);
            });
        } else {
            if (actionType === DataIndexActionType.Translation) {
                const translationV3 = dataItem.translationV3;

                if (value.axis === "X") translationV3.x = value.value;
                else if (value.axis === "Y") translationV3.y = value.value;
                else translationV3.z = value.value;

                const tempTableData = changeDataItem(tableData, dataId, { translationV3: translationV3 });
                setTableData(tempTableData);

                translationPointCloud({ ...dataItem, translationV3: translationV3 });
                return;
            }

            let uuid = "";
            if (pointCloud) {
                uuid = pointCloud.uuid;
            }

            if (actionType === DataIndexActionType.Edit) {
                setSelectedDataIndexRow(dataItem);
                setShowDataIndexEditModal(true);
            } else if (actionType === DataIndexActionType.Show || actionType === DataIndexActionType.Hide) {
                const showValue = actionType === DataIndexActionType.Show;
                if (showValue) {
                    if (pointCloud) {
                        translationPointCloud(dataItem);
                        setPointSourcIdInPointCloud(pointCloud, parseInt(dataItem.id_iterator, 10), false);

                        pointCloud.visible = true;
                        updateActiveProjection(dataItem)
                    } else {
                        uuid = await loadPointCloud(dataItem);
                    }
                    changeDataItemStore(dataItem.data_id, { show: true })
                } else {
                    if (pointCloud.point_source_id) {
                        translationPointCloud(dataItem);
                        setPointSourcIdInPointCloud(pointCloud, parseInt(dataItem.id_iterator, 10), true);
                    } else {
                        pointCloud.visible = false;
                    }

                    uuid = pointCloud.uuid;
                    changeDataItemStore(dataItem.data_id, { show: false })
                }

                const tempTableData = changeDataItem(tableData, dataId, { uuid: uuid, show: showValue, loaded: true });
                setTableData(tempTableData);
            } else if (actionType === DataIndexActionType.ZoomTo) {
                if (pointCloud) {
                    zoomTo(dataItem);
                } else {
                    uuid = await loadPointCloud(dataItem, true);
                }

                const tempTableData = changeDataItem(tableData, dataId, { uuid: uuid, show: true, loaded: true });
                setTableData(tempTableData);
                changeDataItemStore(dataItem.data_id, { show: true })
            }
        }
    };

    const onChangedHandler = (dataId: string, values?: DataIndexItem) => {
        const temp = resetOriginDataList(DataStore.dataItems, { ...values, data_id: dataId });
        DataStore.dataItems = temp;
    };

    return (
        <DataIndexMenuTableContainer>
            <Spin spinning={dataLoading}>
                {tableData && tableData.length > 0 && (
                    <div className={`materialReactTableCustom material_menu_table batch_info ${expand ? 'expand' : ''}`}>
                        <MaterialReactTable table={table} />
                    </div>
                )}
            </Spin>
            <div className='batch-details-operation-btns'>
                <Button disabled={selectedDataItems.length === 0} loading={removerLoader} onClick={removeItems} className='batch_btn'>Remove</Button>
            </div>

            {showDataIndexEditModal && selectedDataIndexRow && (
                <DataIndexMenuEditModal
                    data={selectedDataIndexRow}
                    onCancel={() => {
                        setSelectedDataIndexRow(undefined);
                        setShowDataIndexEditModal(false);
                    }}
                    onChanged={onChangedHandler}
                />
            )}
        </DataIndexMenuTableContainer>
    );
}

export default BatchDetailsTable;
